import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * RawButton — a zero-style <button> atom for functional UI controls.
 *
 * Use this for interactive controls that need custom visual styling:
 * navigation toggles, dropdown triggers, icon-only actions, form resets.
 *
 * Use `Button` (the branded CTA component) for call-to-action buttons
 * that should carry brand styling (primary, secondary, ghost variants).
 *
 * Enforced default: type="button" to prevent accidental form submission.
 * Override with type="submit" for submit actions.
 *
 * @atomic-tier Atom
 * @category Interactive
 */

export type RawButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>

const RawButton = React.forwardRef<HTMLButtonElement, RawButtonProps>(
  ({ className, type = "button", children, ...props }, ref) => (
    <button ref={ref} type={type} className={cn(className)} {...props}>
      {children}
    </button>
  ),
)
RawButton.displayName = "RawButton"

export { RawButton }
